import React, { useState, useEffect } from 'react';
import { Gift, X, Copy, Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ReferralToast({ toast: toastData, onDismiss }) {
  const { isDarkMode } = useTheme();
  const [refLink, setRefLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!toastData) { setVisible(false); return; }
    setVisible(true);
    const token = localStorage.getItem('token');
    if (!token || refLink) return;
    fetch(`${API_BASE}/api/affiliate/my-code`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.ref_link) setRefLink(d.ref_link); })
      .catch(() => {});
  }, [toastData, refLink]);

  const copyLink = () => {
    if (!refLink) return;
    navigator.clipboard.writeText(refLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss?.(), 300);
  };

  if (!toastData) return null;

  const bg = isDarkMode ? '#1e293b' : '#ffffff';
  const border = isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0';
  const text = isDarkMode ? '#f1f5f9' : '#111827';
  const sub = isDarkMode ? '#94a3b8' : '#6b7280';
  const shadow = isDarkMode
    ? '0 20px 40px rgba(0,0,0,0.5)'
    : '0 20px 40px rgba(0,0,0,0.12)';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        width: 340,
        background: bg,
        border,
        borderRadius: 16,
        padding: '16px 18px',
        boxShadow: shadow,
        transform: visible ? 'translateY(0)' : 'translateY(120%)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute', top: 10, right: 10,
          background: 'none', border: 'none', cursor: 'pointer',
          color: sub, padding: 4, borderRadius: 6,
          lineHeight: 0,
        }}
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg, #10b981, #0ea5e9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Gift size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: text, fontSize: 13, fontWeight: 600, lineHeight: 1.4, margin: '0 0 4px' }}>
            {toastData.message}
          </p>
          {refLink && (
            <p style={{ color: sub, fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', margin: '0 0 10px' }}>
              {refLink}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={copyLink}
              disabled={!refLink}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'linear-gradient(135deg, #10b981, #0ea5e9)',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                cursor: refLink ? 'pointer' : 'not-allowed', opacity: refLink ? 1 : 0.5,
              }}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy Referral Link'}
            </button>
            {toastData.buttons === 'remind_later' ? (
              <button
                onClick={handleDismiss}
                style={{
                  background: 'none', border: `1px solid ${border}`,
                  color: sub, borderRadius: 8, padding: '6px 10px',
                  fontSize: 12, cursor: 'pointer',
                }}
              >
                Remind Me Later
              </button>
            ) : (
              <button
                onClick={handleDismiss}
                style={{
                  background: 'none', border: 'none',
                  color: sub, fontSize: 12, cursor: 'pointer', padding: '6px 4px',
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
